import { useQuery } from '@tanstack/react-query';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

export const ruleOptionsPath = (prefix = '') => `${prefix}/user_groups`;
export const customPropertiesRuleOptionsPath = (prefix = '') => `${prefix}/user_groups/custom_property_groups`;
export const ruleOptionsQueryKey = () => ['rules:rule-options'];
export const customPropertiesRuleOptionsQueryKey = () => ['rules:custom-properties-rule-options'];

export type UserGroup = {
	key: string;
	type: 'department' | 'location' | 'team' | 'has_reports?';
	name: string;
	slug: string;
	image: string;
	users: string[];
};

export type CustomPropertyUserGroup = Omit<UserGroup, 'type'> & { type: string };

export type UserGroups = {
	departments: UserGroup[];
	locations: UserGroup[];
	teams: UserGroup[];
	roles: UserGroup[];
};

export type CustomPropertyUserGroups = Record<string, CustomPropertyUserGroup[]>;

export const AttrMap: { [k: string]: string } = {
	department: 'departments',
	team: 'teams',
	location: 'locations',
	'has_reports?': 'roles',
};

/** Inverse of {@link AttrMap}: `CustomPropertyUserGroups` section key → API rule `attr`. */
export const RuleSectionKeyToConditionAttrMap: Record<string, string> = Object.fromEntries(Object.entries(AttrMap).map(([attr, section]) => [section, attr]));

export function ruleSectionKeyToConditionAttr(sectionKey: string): string {
	return RuleSectionKeyToConditionAttrMap[sectionKey] ?? sectionKey;
}

const createUserGroupsFetcher = (http: HttpClient) => async (): Promise<UserGroups> => {
	const res = await http.get<UserGroup[]>(ruleOptionsPath(), {
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	const departments = res.data.filter((f) => f.type === 'department');
	const locations = res.data.filter((f) => f.type === 'location');
	const teams = res.data.filter((f) => f.type === 'team');
	const managerRoles = res.data.filter((f) => f.type === 'has_reports?');

	return {
		departments: departments,
		locations: locations,
		teams: teams,
		roles: managerRoles,
	};
};

const createCustomPropertyUserGroupsFetcher = (http: HttpClient) => async (): Promise<CustomPropertyUserGroups> => {
	const res = await http.get<UserGroup[]>(customPropertiesRuleOptionsPath(), {
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	const groupsByType = res.data.reduce((acc, group) => {
		acc[group.type] = acc[group.type] || [];
		acc[group.type].push(group);
		return acc;
	}, {} as CustomPropertyUserGroups);

	groupsByType.departments = groupsByType.department || [];
	groupsByType.locations = groupsByType.location || [];
	groupsByType.teams = groupsByType.team || [];
	groupsByType.roles = groupsByType['has_reports?'] || [];

	delete groupsByType.department;
	delete groupsByType.location;
	delete groupsByType.team;
	delete groupsByType['has_reports?'];

	return groupsByType;
};

export const useUserGroups = () => {
	return useQuery({
		queryKey: ruleOptionsQueryKey(),
		queryFn: createUserGroupsFetcher(useHttpClient()),
	});
};

export const useCustomPropertyUserGroups = () => {
	return useQuery({
		queryKey: customPropertiesRuleOptionsQueryKey(),
		queryFn: createCustomPropertyUserGroupsFetcher(useHttpClient()),
	});
};

export const loadUserGroups = async () => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: ruleOptionsQueryKey(),
		queryFn: createUserGroupsFetcher(http),
	});
};

export const loadCustomPropertiesUserGroups = async () => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: customPropertiesRuleOptionsQueryKey(),
		queryFn: createCustomPropertyUserGroupsFetcher(http),
	});
};
